import styles from "./Button.module.scss";
import clsx from "clsx";
import {
  ForwardedRef,
  forwardRef,
  MouseEventHandler,
  useCallback,
} from "react";
import { Loading } from "../Loading";

interface ButtonProps {
  text?: string;
  onClick: MouseEventHandler;
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  disabled?: boolean;
  type?: "primary" | "secondary" | "tertiary" | "quaternary" | "danger";
  active?: boolean;
  size?: "small" | "medium" | "large";
  testId?: string;
  id?: string;
  href?: string;
  download?: string;
  className?: string;
  shape?: "square" | "circle";
  // Shows a spinner after the text and disables the button
  loading?: boolean;
  // Spinner to show while loading, defaults to Loading (used by CLJS buttons)
  loadingIcon?: React.ReactNode;
}

// Same logic as in dom/add-test-id (see it for explanations)
function addTestId(testId: string | undefined) {
  if (testId) {
    return {
      "data-testid": testId,
      "data-intercom-target": testId,
    };
  } else {
    return {};
  }
}

// eslint-disable-next-line react/display-name
const Button = forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      size = "medium",
      type = "primary",
      active,
      text,
      testId,
      icon,
      disabled,
      onClick,
      endIcon,
      shape,
      href,
      loading,
      loadingIcon,
      ...props
    },
    ref,
  ) => {
    // Loading button is disabled
    disabled = disabled || loading;
    const Component: React.ElementType = href ? "a" : "button";

    const internalOnClick = useCallback(
      (event: React.MouseEvent) => {
        if (disabled) {
          return;
        }

        onClick?.(event);
      },
      [onClick, disabled],
    );
    return (
      <Component
        {...props}
        role="button"
        {...(Component === "button" && { type: "button" })}
        ref={ref as ForwardedRef<HTMLAnchorElement & HTMLButtonElement>}
        className={clsx(
          className,
          styles.button,
          // Need both CSS class and HTML attribute for certain cases
          disabled && "disabled",
          active && styles["active"],
          type && styles[`button-${type}`],
          shape && styles[`button-${shape}`],
          styles[`button-${size || "medium"}`],
        )}
        // Do not set href on disabled "buttons"
        href={disabled ? undefined : href}
        disabled={disabled}
        onClick={internalOnClick}
        aria-busy={loading || undefined}
        {...addTestId(testId)}
      >
        {icon && (
          <span className={`${styles["icon"]} button-icon`}>{icon}</span>
        )}
        {text && (
          <span className={`${styles["text"]} button-text`}>{text}</span>
        )}
        {loading ? (
          <span className={styles["end-icon"]}>
            {loadingIcon ?? (
              <Loading
                size="text"
                color={
                  type === "primary" || type === "danger"
                    ? "inverted"
                    : "primary"
                }
              />
            )}
          </span>
        ) : (
          endIcon && <span className={styles["end-icon"]}>{endIcon}</span>
        )}
      </Component>
    );
  },
);

export default Button;
